import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MapPin, Phone, Mail, Send, CheckCircle, MessageSquare, 
  Sparkles, ExternalLink, ArrowRight, Clock, ShieldCheck,
  Youtube, Instagram, Facebook, Menu, X, ArrowLeft, Headphones
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PageSeo from "@/components/seo/PageSeo";
import { API } from "@/App";

export default function ContactPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!formData.phone.trim() || formData.phone.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        source: "contact_page"
      };

      const res = await axios.post(`${API}/contact`, payload);
      if (res.data?.success) {
        setSubmitted(true);
        toast.success("Message sent successfully! Our team will get back to you shortly.");
      } else {
        toast.success("Thank you! Your inquiry has been received.");
        setSubmitted(true);
      }
    } catch (err) {
      console.error("Error submitting contact form:", err);
      toast.error(err.response?.data?.detail || "Failed to submit inquiry. Please try again or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: ""
    });
    setSubmitted(false);
  };

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Courses", href: "/#courses" },
    { label: "Blogs", href: "/blog" },
    { label: "Gallery", href: "/gallery" },
    { label: "Contact", href: "/contact" }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-[#00FF88]/30 selection:text-white relative overflow-x-clip" data-testid="contact-page">
      <PageSeo 
        path="/contact" 
        fallbackTitle="Contact Us — JamaDhan Stock Market Institute" 
        fallbackDescription="Get in touch with JamaDhan Stock Market Institute in Jaipur. Inquire about offline classroom batches, online courses, and Drishti AI tool." 
      />

      {/* ═══ AMBIENT GLOW EFFECTS ═══ */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-gradient-to-b from-[#00F0FF]/10 via-[#00FF88]/5 to-transparent blur-[120px] pointer-events-none z-0" />
      <div className="absolute top-[600px] -left-[200px] w-[500px] h-[500px] bg-[#00F0FF]/5 rounded-full blur-[140px] pointer-events-none z-0" />
      <div className="absolute top-[800px] -right-[200px] w-[500px] h-[500px] bg-[#00FF88]/5 rounded-full blur-[140px] pointer-events-none z-0" />

      {/* ═══ HEADER / NAVBAR ═══ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/50' : 'bg-transparent'}`} data-testid="contact-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)] border border-[#00FF88]/30 group-hover:border-[#00FF88]/70 transition-all duration-300">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">JAMADHAN</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                className={`text-sm font-medium tracking-wide transition-colors ${
                  l.href === '/contact' ? 'text-[#00FF88] font-semibold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/drishti-ai" className="text-sm text-[#00F0FF] hover:text-[#00FF88] transition-colors font-semibold">
              Drishti AI
            </Link>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:shadow-[0_0_20px_rgba(0,255,136,0.4)] border border-transparent hover:border-[#00FF88] rounded-sm px-5 transition-all duration-200" 
              data-testid="nav-login-btn"
            >
              Login
            </Button>
          </div>

          <button 
            onClick={() => setMobileMenu(!mobileMenu)} 
            className="md:hidden text-white p-1 hover:text-[#00FF88] transition-colors" 
            data-testid="contact-mobile-menu-btn"
            aria-label="Toggle Menu"
          >
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden bg-[#07090E]/98 backdrop-blur-2xl border-t border-white/10 px-6 py-4 space-y-3">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                onClick={() => setMobileMenu(false)}
                className={`block py-2 text-sm font-medium border-b border-white/5 ${
                  l.href === '/contact' ? 'text-[#00FF88] font-bold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link 
              to="/drishti-ai" 
              onClick={() => setMobileMenu(false)}
              className="block py-2 text-sm text-[#00F0FF] font-semibold border-b border-white/5"
            >
              Drishti AI
            </Link>
            <div className="pt-2">
              <Button 
                onClick={() => { setMobileMenu(false); navigate('/login'); }} 
                className="w-full bg-[#00FF88] text-black font-bold hover:bg-[#00FF88]/90"
              >
                Login
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* ═══ HERO HEADER WITH CANDLESTICK CHART AMBIANCE ═══ */}
      <section className="relative pt-36 pb-16 px-4 sm:px-6 overflow-hidden border-b border-white/5">
        {/* Candlestick backdrop pattern */}
        <div className="absolute inset-0 opacity-15 pointer-events-none flex justify-center items-center overflow-hidden">
          <svg className="w-full h-full min-w-[1200px]" viewBox="0 0 1200 300" fill="none" preserveAspectRatio="none">
            <path d="M50 200 L50 120 M50 140 L70 140 L70 180 L50 180 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M120 220 L120 90 M110 110 L130 110 L130 190 L110 190 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M190 250 L190 140 M180 160 L200 160 L200 230 L180 230 Z" stroke="#FF3B30" strokeWidth="1.5" fill="rgba(255,59,48,0.3)" />
            <path d="M260 210 L260 80 M250 100 L270 100 L270 180 L250 180 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M330 190 L330 60 M320 80 L340 80 L340 160 L320 160 Z" stroke="#00F0FF" strokeWidth="1.5" fill="rgba(0,240,255,0.3)" />
            <path d="M400 230 L400 110 M390 130 L410 130 L410 200 L390 200 Z" stroke="#FF3B30" strokeWidth="1.5" fill="rgba(255,59,48,0.3)" />
            <path d="M470 170 L470 50 M460 70 L480 70 L480 140 L460 140 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M540 180 L540 70 M530 90 L550 90 L550 150 L530 150 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M610 240 L610 100 M600 120 L620 120 L620 210 L600 210 Z" stroke="#FF3B30" strokeWidth="1.5" fill="rgba(255,59,48,0.3)" />
            <path d="M680 200 L680 80 M670 100 L690 100 L690 170 L670 170 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M750 160 L750 40 M740 60 L760 60 L760 130 L740 130 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M820 190 L820 90 M810 110 L830 110 L830 170 L810 170 Z" stroke="#FF3B30" strokeWidth="1.5" fill="rgba(255,59,48,0.3)" />
            <path d="M890 220 L890 70 M880 90 L900 90 L900 180 L880 180 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M960 180 L960 50 M950 70 L970 70 L970 150 L950 150 Z" stroke="#00F0FF" strokeWidth="1.5" fill="rgba(0,240,255,0.3)" />
            <path d="M1030 210 L1030 90 M1020 110 L1040 110 L1040 180 L1020 180 Z" stroke="#00FF88" strokeWidth="1.5" fill="rgba(0,255,136,0.3)" />
            <path d="M1100 230 L1100 100 M1090 120 L1110 120 L1110 200 L1090 200 Z" stroke="#FF3B30" strokeWidth="1.5" fill="rgba(255,59,48,0.3)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 15 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-semibold tracking-wider uppercase mb-5 font-mono"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#00FF88]" /> Connect with India's Premier Trading Institute
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-6xl font-black text-white font-outfit tracking-tight mb-4 drop-shadow-[0_2px_20px_rgba(0,255,136,0.2)]"
          >
            Contact us
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 15 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed"
          >
            Have questions about our mentorship batches, classroom training in Jaipur, or Drishti AI? Reach out to our dedicated team.
          </motion.p>
        </div>
      </section>

      {/* ═══ MAIN 2-COLUMN SECTION: GET IN TOUCH & INQUIRE FORM ═══ */}
      <section className="py-16 px-4 sm:px-6 relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          
          {/* ── LEFT COLUMN: GET IN TOUCH WITH US ── */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 space-y-6"
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-outfit tracking-tight mb-2">
                Get in touch with us
              </h2>
              <p className="text-sm text-[#94A3B8]">
                Visit our state-of-the-art Jaipur trading center or reach out digitally anytime.
              </p>
            </div>

            {/* Address Card */}
            <div className="group p-6 rounded-xl border border-white/[0.08] bg-[#0A0D14]/80 backdrop-blur-md hover:border-[#00F0FF]/40 transition-all duration-300 shadow-lg shadow-black/40 hover:shadow-[0_0_30px_rgba(0,240,255,0.1)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#00FF88]/10 border border-[#00F0FF]/40 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,240,255,0.2)] group-hover:scale-105 transition-transform">
                  <MapPin className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <h3 className="text-base font-bold text-white font-outfit tracking-wide flex items-center gap-2">
                    Address
                  </h3>
                  <p className="text-sm text-[#CBD5E1] leading-relaxed">
                    3rd Floor, JamaDhan Stock Market Institute, Ajmer Road, Opposite MG Motors, Heera Nagar, Tagore Nagar, Jaipur, Rajasthan, 302021
                  </p>
                  <div className="pt-2">
                    <a 
                      href="https://maps.google.com/?q=JamaDhan+Stock+Market+Institute,+Ajmer+Road,+Heera+Nagar,+Jaipur" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-[#00FF88] hover:text-[#00FF88]/80 font-semibold transition-colors"
                    >
                      Get Directions <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Phone Card */}
            <div className="group p-6 rounded-xl border border-white/[0.08] bg-[#0A0D14]/80 backdrop-blur-md hover:border-[#00FF88]/40 transition-all duration-300 shadow-lg shadow-black/40 hover:shadow-[0_0_30px_rgba(0,255,136,0.1)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00FF88]/20 to-[#00F0FF]/10 border border-[#00FF88]/40 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,255,136,0.2)] group-hover:scale-105 transition-transform">
                  <Phone className="w-6 h-6 text-[#00FF88]" />
                </div>
                <div className="space-y-1 flex-1">
                  <h3 className="text-base font-bold text-white font-outfit tracking-wide">
                    Contact
                  </h3>
                  <p className="text-base font-mono font-semibold text-white">
                    <a href="tel:+919257059361" className="hover:text-[#00FF88] transition-colors">
                      +91-9257059361
                    </a>
                  </p>
                  <p className="text-xs text-[#64748B] flex items-center gap-1 pt-1">
                    <Clock className="w-3.5 h-3.5 text-[#00FF88]" /> Mon - Sat: 9:00 AM – 7:00 PM IST
                  </p>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="group p-6 rounded-xl border border-white/[0.08] bg-[#0A0D14]/80 backdrop-blur-md hover:border-[#00F0FF]/40 transition-all duration-300 shadow-lg shadow-black/40 hover:shadow-[0_0_30px_rgba(0,240,255,0.1)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#0080FF]/20 border border-[#00F0FF]/40 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,240,255,0.2)] group-hover:scale-105 transition-transform">
                  <Mail className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div className="space-y-1 flex-1">
                  <h3 className="text-base font-bold text-white font-outfit tracking-wide">
                    Email
                  </h3>
                  <p className="text-sm font-medium text-white">
                    <a href="mailto:contactus@jamadhan.com" className="hover:text-[#00F0FF] transition-colors">
                      contactus@jamadhan.com
                    </a>
                  </p>
                  <p className="text-xs text-[#64748B]">
                    Support & Course Enrollment Desk
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media Card */}
            <div className="group p-6 rounded-xl border border-white/[0.08] bg-[#0A0D14]/80 backdrop-blur-md hover:border-white/20 transition-all duration-300 shadow-lg shadow-black/40">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                  <MessageSquare className="w-6 h-6 text-[#CBD5E1]" />
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="text-base font-bold text-white font-outfit tracking-wide">
                    Social Media
                  </h3>
                  <div className="flex items-center gap-3 pt-1">
                    {[
                      { label: "YouTube", href: "https://www.youtube.com/@jamadhan", color: "bg-[#FF0000]/10 hover:bg-[#FF0000]/20 text-[#FF0000] border-[#FF0000]/30", icon: <Youtube className="w-4 h-4" /> },
                      { label: "Instagram", href: "https://www.instagram.com/jama_dhan/", color: "bg-[#FF4D8D]/10 hover:bg-[#FF4D8D]/20 text-[#FF4D8D] border-[#FF4D8D]/30", icon: <Instagram className="w-4 h-4" /> },
                      { label: "Facebook", href: "https://www.facebook.com/jamadhaninstitute", color: "bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-[#1877F2] border-[#1877F2]/30", icon: <Facebook className="w-4 h-4" /> },
                      { 
                        label: "Telegram", 
                        href: "https://t.me/jamadhanofficial", 
                        color: "bg-[#26A5E4]/10 hover:bg-[#26A5E4]/20 text-[#26A5E4] border-[#26A5E4]/30", 
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.056zm4.962 7.224c.451-.01.636.37.656.605.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.014-.15-.056-.212-.305-.188-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                          </svg>
                        ) 
                      },
                    ].map((s) => (
                      <a 
                        key={s.label} 
                        href={s.href} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        aria-label={s.label}
                        className={`w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-200 hover:-translate-y-1 shadow-sm ${s.color}`}
                      >
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </motion.div>

          {/* ── RIGHT COLUMN: INQUIRE FORM ── */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <div className="relative rounded-2xl p-6 sm:p-8 border border-white/[0.12] bg-gradient-to-br from-[#121624]/90 via-[#0B0E17]/95 to-[#06080E]/95 backdrop-blur-xl shadow-2xl shadow-black/80 overflow-hidden">
              {/* Subtle trading bull silhouette / aesthetic mesh watermark */}
              <div className="absolute right-0 bottom-0 w-64 h-64 opacity-5 pointer-events-none transform translate-x-12 translate-y-12">
                <svg viewBox="0 0 200 200" fill="white">
                  <path d="M40,160 Q80,40 120,60 T180,30 L170,90 Q140,120 100,140 Z" />
                </svg>
              </div>

              {/* Glowing decorative corner */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#00F0FF]/15 to-transparent blur-2xl pointer-events-none" />

              <div className="relative z-10">
                <div className="mb-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white font-outfit tracking-tight">
                    Inquire Form
                  </h3>
                  <p className="text-xs sm:text-sm text-[#94A3B8] mt-1">
                    Fill in your details and our senior mentor will call you back.
                  </p>
                </div>

                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div 
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="py-12 px-4 text-center space-y-4"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/40 text-[#00FF88] flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(0,255,136,0.3)]">
                        <CheckCircle className="w-9 h-9" />
                      </div>
                      <h4 className="text-2xl font-bold text-white font-outfit">
                        Inquiry Received!
                      </h4>
                      <p className="text-sm text-[#CBD5E1] max-w-md mx-auto leading-relaxed">
                        Thank you, <span className="text-[#00FF88] font-semibold">{formData.firstName}</span>. Our team at JamaDhan will get in touch with you at <span className="text-white font-mono">{formData.phone || formData.email}</span> shortly.
                      </p>
                      <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Button 
                          onClick={resetForm}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10 text-xs px-4"
                        >
                          Send Another Message
                        </Button>
                        <a 
                          href="https://wa.me/919257059361?text=Hello%20JamaDhan,%20I%20have%20an%20inquiry%20regarding%20trading%20courses." 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#25D366] text-black font-bold text-xs px-4 py-2 rounded-md hover:bg-[#25D366]/90 transition-colors"
                        >
                          Chat on WhatsApp Now →
                        </a>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.form 
                      key="form"
                      onSubmit={handleSubmit} 
                      className="space-y-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* First & Last Name */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-[#CBD5E1] tracking-wide">
                            First Name <span className="text-[#FF3B30]">*</span>
                          </label>
                          <Input 
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="John"
                            required
                            className="bg-black/40 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] h-11 text-sm rounded-lg"
                            data-testid="contact-input-firstname"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-[#CBD5E1] tracking-wide">
                            Last Name
                          </label>
                          <Input 
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe"
                            className="bg-black/40 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] h-11 text-sm rounded-lg"
                            data-testid="contact-input-lastname"
                          />
                        </div>
                      </div>

                      {/* Email Address */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#CBD5E1] tracking-wide">
                          Email Address <span className="text-[#FF3B30]">*</span>
                        </label>
                        <Input 
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@example.com"
                          required
                          className="bg-black/40 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] h-11 text-sm rounded-lg"
                          data-testid="contact-input-email"
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#CBD5E1] tracking-wide">
                          Phone Number <span className="text-[#FF3B30]">*</span>
                        </label>
                        <div className="flex rounded-lg overflow-hidden border border-white/10 bg-black/40 focus-within:border-[#00FF88] focus-within:ring-1 focus-within:ring-[#00FF88]">
                          <div className="flex items-center gap-1 px-3 bg-white/[0.04] border-r border-white/10 text-xs font-mono text-[#CBD5E1]">
                            <span>🇮🇳</span>
                            <span>+91</span>
                          </div>
                          <input 
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="98765 43210"
                            required
                            className="w-full bg-transparent px-3 py-2.5 text-sm text-white placeholder:text-[#52525B] outline-none font-mono"
                            data-testid="contact-input-phone"
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-[#CBD5E1] tracking-wide">
                            Message
                          </label>
                          <span className="text-[11px] text-[#64748B] font-mono">
                            {formData.message.length} / 500
                          </span>
                        </div>
                        <Textarea 
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          maxLength={500}
                          rows={3}
                          placeholder="Tell us what you're looking to learn or any queries about our courses..."
                          className="bg-black/40 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] text-sm rounded-lg resize-none min-h-[90px]"
                          data-testid="contact-input-message"
                        />
                      </div>

                      {/* Privacy & Security Note */}
                      <div className="flex items-center gap-2 text-[11px] text-[#64748B] pt-1">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#00FF88]" />
                        <span>100% Privacy. Your details will never be shared or spammed.</span>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2">
                        <Button 
                          type="submit" 
                          disabled={submitting}
                          className="w-full bg-gradient-to-r from-[#00FF88] to-[#00F0FF] text-black font-extrabold font-outfit text-sm tracking-wide h-12 rounded-lg hover:shadow-[0_0_25px_rgba(0,255,136,0.5)] transition-all duration-300 transform active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="contact-submit-btn"
                        >
                          {submitting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                              <span>Sending Inquiry...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span>Send Message</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ═══ GOOGLE MAP EMBED SECTION ═══ */}
      <section className="py-10 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0A0D14] shadow-2xl shadow-black/80">
          <div className="p-4 sm:p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gradient-to-r from-white/[0.03] to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#00FF88]/10 border border-[#00FF88]/30 flex items-center justify-center text-[#00FF88]">
                <MapPin className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white font-outfit">
                  JamaDhan Stock Market Institute — Jaipur Center
                </h4>
                <p className="text-xs text-[#94A3B8]">
                  Opposite MG Motors, Ajmer Road, Heera Nagar, Jaipur
                </p>
              </div>
            </div>
            <a 
              href="https://maps.google.com/?q=JamaDhan+Stock+Market+Institute,+Ajmer+Road,+Heera+Nagar,+Jaipur" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#00F0FF] hover:text-[#00FF88] font-semibold transition-colors"
            >
              Open in Google Maps <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="w-full h-[380px] sm:h-[450px] relative bg-[#121624]">
            <iframe
              title="JamaDhan Stock Market Institute Location"
              src="https://maps.google.com/maps?q=JamaDhan+Stock+Market+Institute,+Ajmer+Road,+Tagore+Nagar,+Jaipur,+Rajasthan+302021&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "contrast(1.05)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* ═══ FLOATING ACTION BUTTONS (WHATSAPP & PHONE CALL) ═══ */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        {/* Call Floating Button */}
        <a 
          href="tel:+919257059361" 
          aria-label="Call JamaDhan"
          className="w-13 h-13 rounded-full bg-[#1877F2] text-white flex items-center justify-center shadow-lg shadow-blue-500/40 hover:scale-110 transition-transform duration-200 border-2 border-white/20"
          style={{ width: "52px", height: "52px" }}
        >
          <Phone className="w-6 h-6" />
        </a>

        {/* WhatsApp Floating Button */}
        <a 
          href="https://wa.me/919257059361?text=Hi%20JamaDhan,%20I%20want%20to%20know%20more%20about%20your%20trading%20programs." 
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="w-13 h-13 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 border-2 border-white/20 animate-pulse-slow"
          style={{ width: "52px", height: "52px" }}
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
            <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.59 5.93L.12 24l6.36-1.67a11.82 11.82 0 0 0 5.6 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.42ZM12.09 21.76h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.5-5.25C2.21 6.42 6.64 2 12.08 2c2.64 0 5.11 1.03 6.98 2.89a9.77 9.77 0 0 1 2.89 6.97c0 5.45-4.43 9.9-9.86 9.9Zm5.4-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
          </svg>
        </a>
      </div>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-white/10 bg-[#07090E] pt-14 pb-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Logo & Institute Info */}
          <div className="space-y-4 md:col-span-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded overflow-hidden shadow-[0_0_18px_rgba(0,255,136,0.3)] border border-[#00FF88]/30">
                <img src="/jamadhan-logo.png" alt="JamaDhan" className="w-full h-full object-cover" />
              </div>
              <span className="text-xl font-bold text-white font-outfit tracking-tight">JAMADHAN</span>
            </div>
            <p className="text-sm text-[#94A3B8] max-w-md leading-relaxed">
              India's Premier Stock Market Learning Institute & AI-Powered Market Intelligence System. Empowering traders with institutional demand-supply zone strategies.
            </p>
            <div className="text-xs text-[#64748B]">
              ISO 21001:2018 Certified Educational Organization
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h5 className="text-sm font-bold text-white font-outfit uppercase tracking-wider mb-4">
              Quick Links
            </h5>
            <ul className="space-y-2.5 text-sm text-[#CBD5E1]">
              <li><Link to="/" className="hover:text-[#00FF88] transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-[#00FF88] transition-colors">About Us</Link></li>
              <li><Link to="/#courses" className="hover:text-[#00FF88] transition-colors">Courses</Link></li>
              <li><Link to="/gallery" className="hover:text-[#00FF88] transition-colors">Gallery</Link></li>
              <li><Link to="/contact" className="hover:text-[#00FF88] transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Legal & Compliance */}
          <div>
            <h5 className="text-sm font-bold text-white font-outfit uppercase tracking-wider mb-4">
              Legal & Support
            </h5>
            <ul className="space-y-2.5 text-sm text-[#CBD5E1]">
              <li><Link to="/privacy-policy" className="hover:text-[#00FF88] transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-[#00FF88] transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/refunds" className="hover:text-[#00FF88] transition-colors">Refund Policy</Link></li>
              <li><Link to="/support" className="hover:text-[#00FF88] transition-colors">Student Helpdesk</Link></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#64748B]">
          <p>© {new Date().getFullYear()} JamaDhan Stock Market Institute, Jaipur. All rights reserved.</p>
          <p className="font-mono">Empowering Traders Across India</p>
        </div>

        {/* Regulatory & Risk Disclaimer */}
        <div className="max-w-7xl mx-auto pt-4 mt-4 border-t border-white/[0.04] text-[11px] leading-relaxed text-[#64748B] text-center sm:text-left">
          <p>
            JamaDhan is an educational institute that provides stock market training only. We are not registered with SEBI as an investment adviser or research analyst, and nothing on this website, in our classes or in our community groups is investment advice or a recommendation to buy, sell or hold any security. Trading and investing in securities, derivatives, commodities and currencies involve a high risk of loss, and securities market investments are subject to market risks. Examples shown are for teaching purposes only. Past performance and individual student experiences are not indicative of future results.
          </p>
        </div>
      </footer>

    </div>
  );
}
